/**
 * GradeEstimateNotice — every AI grade is an ESTIMATE, and says so.
 *
 * ⛔ WHY THIS IS NOT DECORATION. Measured on 305 held-out books against two graders:
 *
 *     band        Gemini +/-1     CV +/-1
 *     0.5-2.0        0.318         0.151
 *     2.5-4.0        0.162         0.242
 *     8.5+           0.533         0.608
 *
 * A single cover photo carries enough signal to place a high-grade book and nowhere near enough
 * to place a low-grade one: below ~4.0 both graders are wrong more than four times in five, and
 * they disagree about which one is less wrong. That is a limit of the input, not a model defect
 * a retrain removes -- interior pages, spine, and handling are simply not in the photograph.
 *
 * So the number is a starting point for a conversation with a grader, never a substitute for
 * one. Presenting it without that framing invites someone to price or sell against it.
 *
 * The wording lives HERE, once. Five components render a grade; five copies of a disclaimer
 * drift, and the one that drifts is the one nobody re-reads.
 */
import { Info } from 'lucide-react';

export default function GradeEstimateNotice({ className = '' }) {
  return (
    <div
      className={`flex items-start gap-2 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg ${className}`}
      data-testid="grade-estimate-notice"
    >
      <Info className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <div className="text-xs text-zinc-400">
        <p className="text-zinc-300 font-medium mb-0.5">This is an estimate, not a certified grade.</p>
        <p>
          It is predicted from a single cover photo, so it cannot see interior pages, the spine,
          or handling. Accuracy is best on high-grade copies and drops sharply on lower grades.
          Use it as a starting point &mdash; for a grade you can sell or insure against, submit
          the book for professional grading.
        </p>
      </div>
    </div>
  );
}

// No propTypes: prop-types is not a declared dependency of this package -- the components that
// import it rely on a transitive install, and this file is not going to add to that.
//
// No `compact` variant either. One was written and then removed: nothing called it, so it was
// untested API surface added on a guess about a future caller. When a panel needs a one-line
// form, it can be added alongside the caller that proves the shape.
